import { useCallback, useEffect, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { subscribeTauriEvent } from '../utils/safeTauriEvent';
import { isTauri } from '../utils/tauri';

/**
 * SSH host-key events from Rust (`src-tauri/src/ssh_known_hosts`).
 *
 *  - `ssh:host_key_prompt` — an unknown server during a connect a person
 *    started. Rust is blocked on the verdict (90 s, then it cancels). Several
 *    can be pending for DIFFERENT hosts, so they queue; the same host+key is
 *    already de-duplicated Rust-side.
 *  - `ssh:host_key_prompt_closed` — timed out; drop the card.
 *  - `ssh:host_key_changed` — a saved server presented a different key. The
 *    connection has ALREADY been refused; this is only the warning. Warnings
 *    queue like prompts (one per host:port + key), so a second one cannot
 *    silently replace the first.
 *
 * `conflict` is true when this host:port has shown MORE THAN ONE fingerprint
 * since its first pending prompt — two connections saw two different servers,
 * so at least one of them is not the real one. The fingerprints are remembered
 * until EVERY prompt for that host:port is resolved: answering the first card
 * must not make the second (possibly the attacker's) look like a normal one.
 *
 * The hook decides nothing: it shows what Rust sent and posts the verdict back.
 */
const hostKey = (p) => `${p.host}:${p.port}`;

/** Add a prompt; remember its fingerprint for its host:port. */
export function addPrompt(state, p) {
  if (state.prompts.some((x) => x.requestId === p.requestId)) return state;
  const k = hostKey(p);
  const fps = state.seen[k] || [];
  return {
    prompts: [...state.prompts, p],
    seen: { ...state.seen, [k]: fps.includes(p.fingerprint) ? fps : [...fps, p.fingerprint] },
  };
}

/** Drop a prompt; forget a host's fingerprints only when none of its prompts remain. */
export function removePrompt(state, requestId) {
  const gone = state.prompts.find((x) => x.requestId === requestId);
  if (!gone) return state;
  const prompts = state.prompts.filter((x) => x.requestId !== requestId);
  const k = hostKey(gone);
  if (prompts.some((x) => hostKey(x) === k)) return { prompts, seen: state.seen };
  const { [k]: _forgotten, ...seen } = state.seen;
  return { prompts, seen };
}

export function useSshHostKeyEvents() {
  const [promptState, setPromptState] = useState({ prompts: [], seen: {} });
  const [changedList, setChangedList] = useState([]);

  useEffect(() => {
    if (!isTauri()) return undefined;
    const subs = [
      subscribeTauriEvent('ssh:host_key_prompt', (event) => {
        const p = event?.payload || {};
        if (p.requestId == null || !p.fingerprint) return;
        setPromptState((st) => addPrompt(st, p));
      }),
      subscribeTauriEvent('ssh:host_key_prompt_closed', (event) => {
        const id = event?.payload?.requestId;
        setPromptState((st) => removePrompt(st, id));
      }),
      subscribeTauriEvent('ssh:host_key_changed', (event) => {
        const p = event?.payload || {};
        if (!p.host || !p.fingerprint) return;
        const same = (x) =>
          x.host === p.host && x.port === p.port && x.fingerprint === p.fingerprint;
        setChangedList((list) => (list.some(same) ? list : [...list, p]));
      }),
    ];
    return () => subs.forEach((s) => s.unlisten());
  }, []);

  const respond = useCallback(async (requestId, accept) => {
    setPromptState((st) => removePrompt(st, requestId));
    try {
      await invoke('ssh_host_key_respond', { requestId, accept: !!accept });
    } catch (e) {
      // Timed out already: Rust cancelled the connection, nothing to undo.
      console.warn('[ssh] host key response failed:', e);
    }
  }, []);

  const dismissChanged = useCallback(() => setChangedList((list) => list.slice(1)), []);

  const prompt = promptState.prompts[0] || null;
  const conflict = !!prompt && (promptState.seen[hostKey(prompt)] || []).length > 1;

  return { prompt, conflict, respond, changed: changedList[0] || null, dismissChanged };
}
